import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ActionResult } from "../lib/types";

/**
 * The payoff for the entire loop, so it gets the whole screen and a moment of its
 * own. Nothing is tappable for the first beat -- the point is to make you look up
 * from the phone, not to hurry you on to the next card.
 */
export default function Conquest({
  result,
  visitEnded,
  onDismiss,
}: {
  result: ActionResult | null;
  visitEnded: boolean;
  onDismiss: () => void;
}) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!result) navigate("/", { replace: true });
  }, [result, navigate]);

  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), 1400);
    return () => window.clearTimeout(timer);
  }, []);

  if (!result) return null;

  const place = result.place;
  const inflation = place.target_points - place.starting_target;
  const conqueredOn = place.conquered_at
    ? new Date(place.conquered_at).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="conquest">
      <div className="conquest__glow" aria-hidden />

      <div className="conquest__body">
        <p className="conquest__kicker">Erobert</p>
        <h1 className="conquest__place">{place.name}</h1>
        {conqueredOn ? <p className="conquest__date">{conqueredOn}</p> : null}

        <div className="conquest__stats">
          <div>
            <strong>{place.earned_points}</strong>
            <span>Punkte verdient</span>
          </div>
          <div>
            <strong>{place.target_points}</strong>
            <span>Ziel erreicht</span>
          </div>
          {inflation > 0 ? (
            <div>
              <strong>+{inflation}</strong>
              <span>selbst erschwert</span>
            </div>
          ) : null}
        </div>

        <p className="conquest__line">
          {inflation > 0
            ? `Du hast mit ${place.name} gerungen — jetzt steht der Ort für immer auf der Karte.`
            : `${place.name} steht für immer auf der Karte.`}
        </p>

        {visitEnded ? (
          <p className="conquest__note">
            Der Besuch ist beendet. Deine übrigen Karten bleiben in {place.name} liegen, falls
            du wiederkommst.
          </p>
        ) : null}
      </div>

      <div className={`conquest__actions ${ready ? "is-ready" : ""}`}>
        <button
          className="btn btn--lg btn--block"
          onClick={() => {
            onDismiss();
            navigate(`/places/${place.id}`, { replace: true });
          }}
        >
          Zum Archiv
        </button>
        {/* The visit ended with the conquest, so there is nothing to go back to
            playing -- this just closes the moment. */}
        <button
          className="btn btn--ghost btn--block"
          onClick={() => {
            onDismiss();
            navigate("/", { replace: true });
          }}
        >
          Fertig
        </button>
      </div>
    </div>
  );
}
